import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { formatPhoneNumber, formatEmail } from "../../utils/formatters";
import "./AddLeadModal.css";
import { handleAuthError, handleGenericError } from "../../utils/errorHandler";

function AddLeadModal({ isOpen, onClose, onLeadAdded }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
    instagram_username: "",
    linkedin_profile: "",
    status: "Lead",
  });
  const [contactFields, setContactFields] = useState({
    phone_number: false,
    email: false,
    instagram_username: false,
    linkedin_profile: false,
  });
  const [assignToMe, setAssignToMe] = useState(true);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [managedReps, setManagedReps] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState("");

  // Fetch managed reps when modal opens
  useEffect(() => {
    const fetchManagedReps = async () => {
      if (!isOpen) return;

      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const response = await fetch(
          getApiEndpoint("/v1/company-downline/reps-by-manager"),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();

          // Handle different possible response structures
          let reps = [];
          if (Array.isArray(result)) {
            reps = result;
          } else if (result.data && Array.isArray(result.data)) {
            reps = result.data;
          } else if (
            result.data &&
            result.data.reps &&
            Array.isArray(result.data.reps)
          ) {
            reps = result.data.reps;
          }

          setManagedReps(reps);
        }
      } catch (error) {}
    };

    fetchManagedReps();
  }, [isOpen, getVerifiedJWT]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        first_name: "",
        last_name: "",
        phone_number: "",
        email: "",
        instagram_username: "",
        linkedin_profile: "",
        status: "Lead",
      });
      setContactFields({
        phone_number: false,
        email: false,
        instagram_username: false,
        linkedin_profile: false,
      });
      setAssignToMe(true);
      setSelectedRecruiter(null);
      setSearchTerm("");
      setShowDropdown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = e => {
    const { name, value } = e.target;

    // Apply formatting based on field type
    if (name === "phone_number") {
      setFormData(prev => ({
        ...prev,
        [name]: formatPhoneNumber(value),
      }));
    } else if (name === "email") {
      setFormData(prev => ({
        ...prev,
        [name]: formatEmail(value),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCheckboxChange = fieldName => {
    setContactFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));

    // Clear field value if unchecking
    if (contactFields[fieldName]) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: "",
      }));
    }
  };

  const handleAssignToMeChange = () => {
    setAssignToMe(!assignToMe);
    if (!assignToMe) {
      // If switching back to "assign to me", clear the selected recruiter
      setSelectedRecruiter(null);
      setSearchTerm("");
      setShowDropdown(false);
    }
  };

  const handleRecruiterSearch = e => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleSelectRecruiter = rep => {
    setSelectedRecruiter(rep);
    setSearchTerm(`${rep.first_name} ${rep.last_name}`);
    setShowDropdown(false);
  };

  const filteredReps = Array.isArray(managedReps)
    ? managedReps.filter(rep => {
        const fullName = `${rep.first_name} ${rep.last_name}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
      })
    : [];

  const handleSubmit = async (e, forceDuplicate = false) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.first_name) {
      showError("First name is required");
      return;
    }

    // Validate recruiter selection if not assigning to self
    if (!assignToMe && !selectedRecruiter) {
      showError("Please select a recruiter");
      return;
    }

    setLoading(true);

    const jwt = await getVerifiedJWT();
    if (!jwt) {
      handleAuthError("Invalid jwt");
      setLoading(false);
      return;
    }

    try {
      // Prepare the request body
      const requestBody = {
        ...formData,
        // Remove formatting from phone number before submitting (keep only digits)
        phone_number: formData.phone_number.replace(/\D/g, ""),
        // If not assigning to me, include the selected recruiter's rep_id as a string
        ...(!assignToMe &&
          selectedRecruiter && {
            assigned_rep_id: selectedRecruiter.rep_id.toString(),
          }),
        // Add force_duplicate flag if retrying after duplicate warning
        ...(forceDuplicate && { force_duplicate: true }),
      };

      const response = await fetch(getApiEndpoint("/v1/recruiting/leads"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(requestBody),
      });

      // Parse response data first
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        handleGenericError(parseError, "Failed to add lead");
        setLoading(false);
        return;
      }

      if (response.ok) {
        showSuccess("Lead added successfully!");

        // Reset form
        setFormData({
          first_name: "",
          last_name: "",
          phone_number: "",
          email: "",
          instagram_username: "",
          linkedin_profile: "",
          status: "Lead",
        });

        // Close duplicate confirmation if it was open
        setShowDuplicateConfirm(false);
        setDuplicateMessage("");

        // Notify parent component
        if (onLeadAdded) {
          onLeadAdded(result.data);
        }

        onClose();
      } else if (response.status === 409) {
        if (!forceDuplicate) {
          // Duplicate recruit detected - show confirmation modal
          const message =
            result.message ||
            "A recruit with this name already exists for the selected recruiter";
          setDuplicateMessage(message);
          setShowDuplicateConfirm(true);
        } else {
          // Force duplicate also got 409 - show error
          showError("Failed to add duplicate lead");
        }
      } else {
        // Other error
        showError(result.message || "Failed to add lead");
      }
    } catch (error) {
      handleGenericError(error, "Network error - please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleProceedWithDuplicate = async () => {
    // Create synthetic event to pass to handleSubmit
    const syntheticEvent = { preventDefault: () => {} };
    await handleSubmit(syntheticEvent, true);
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateConfirm(false);
    setDuplicateMessage("");
  };

  if (!isOpen) return null;

  return (
    <div className="add-lead-modal-overlay" onClick={onClose}>
      <div
        className="add-lead-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div className="add-lead-modal-header">
          <h2>Add New Lead</h2>
          <button className="add-lead-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-lead-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Enter first name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="status">Stage</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Lead">Contacted</option>
              <option value="Initial Meeting">1st Meeting Occurred</option>
              <option value="Second Meeting">2nd Meeting Occurred</option>
              <option value="Offers Sent">Offer Sent</option>
              <option value="Signed">Signed</option>
            </select>
          </div>

          <div className="contact-info-section">
            <h3 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
              Contact Information
            </h3>

            {/* Phone Number */}
            <div className="form-group-with-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={contactFields.phone_number}
                  onChange={() => handleCheckboxChange("phone_number")}
                />
                <span>Phone Number</span>
              </label>
              {contactFields.phone_number && (
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                />
              )}
            </div>

            {/* Email */}
            <div className="form-group-with-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={contactFields.email}
                  onChange={() => handleCheckboxChange("email")}
                />
                <span>Email</span>
              </label>
              {contactFields.email && (
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                />
              )}
            </div>

            {/* Instagram */}
            <div className="form-group-with-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={contactFields.instagram_username}
                  onChange={() => handleCheckboxChange("instagram_username")}
                />
                <span>Instagram Username</span>
              </label>
              {contactFields.instagram_username && (
                <input
                  type="text"
                  id="instagram_username"
                  name="instagram_username"
                  value={formData.instagram_username}
                  onChange={handleChange}
                  placeholder="@username"
                />
              )}
            </div>

            {/* LinkedIn */}
            <div className="form-group-with-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={contactFields.linkedin_profile}
                  onChange={() => handleCheckboxChange("linkedin_profile")}
                />
                <span>LinkedIn Profile</span>
              </label>
              {contactFields.linkedin_profile && (
                <input
                  type="url"
                  id="linkedin_profile"
                  name="linkedin_profile"
                  value={formData.linkedin_profile}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/username"
                />
              )}
            </div>
          </div>

          {/* Recruiter Assignment Section */}
          <div className="recruiter-section">
            <h3 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
              Recruiter Assignment
            </h3>
            <div className="form-group-with-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={assignToMe}
                  onChange={handleAssignToMeChange}
                />
                <span>Assign to me</span>
              </label>
              {!assignToMe && (
                <div className="recruiter-search-container">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleRecruiterSearch}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search for a rep..."
                    className="recruiter-search-input"
                  />
                  {showDropdown && filteredReps.length > 0 && (
                    <div className="recruiter-dropdown">
                      {filteredReps.map(rep => (
                        <div
                          key={rep.rep_id}
                          className="recruiter-dropdown-item"
                          onClick={() => handleSelectRecruiter(rep)}
                        >
                          <div className="recruiter-name">
                            {rep.first_name} {rep.last_name}
                          </div>
                          {rep.email && (
                            <div className="recruiter-email">{rep.email}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {!assignToMe && showDropdown && filteredReps.length === 0 && (
                    <div className="recruiter-dropdown">
                      <div className="recruiter-dropdown-empty">
                        {managedReps.length === 0
                          ? "No reps available to assign"
                          : searchTerm
                            ? "No reps found matching your search"
                            : "Start typing to search for reps"}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Lead"}
            </button>
          </div>
        </form>

        {/* Duplicate Confirmation Modal */}
        {showDuplicateConfirm && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "12px",
            }}
            onClick={handleCancelDuplicate}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "1.5rem",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <span
                  className="material-icons"
                  style={{ fontSize: "2.5rem", color: "#f59e0b" }}
                >
                  warning
                </span>
                <h3
                  style={{ margin: 0, fontSize: "1.25rem", color: "#1f2937" }}
                >
                  Duplicate Recruit Detected
                </h3>
              </div>
              <p
                style={{
                  color: "#6b7280",
                  lineHeight: "1.6",
                  marginBottom: "1.5rem",
                }}
              >
                {duplicateMessage}
              </p>
              <p
                style={{
                  color: "#6b7280",
                  lineHeight: "1.6",
                  marginBottom: "1.5rem",
                }}
              >
                Do you want to proceed anyway?
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelDuplicate}
                  disabled={loading}
                  style={{ minWidth: "100px" }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleProceedWithDuplicate}
                  disabled={loading}
                  style={{ minWidth: "100px" }}
                >
                  {loading ? "Adding..." : "Proceed"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddLeadModal;
